/**
 * What a persisted filter stage's value may contain. Lives beside the column-key
 * helpers because it is the same class of contract: the shape the panel config
 * holds, shared by every writer of it — the filter bar, and the AI tool that
 * writes filters without going through the bar.
 */

/**
 * True for a decimal numeric string.
 *
 * Hex, octal, and binary literals are rejected even though `Number()` accepts
 * them and the search engine would filter on them: numeric filter values are
 * decimal by policy. The rule is the filter bar's original input guard, kept so
 * the bar and the AI tool accept the same values.
 */
export function isFiniteNumericString(value: string): boolean {
  const trimmed = value.trim();
  if (/^0[xXoObB]/.test(trimmed)) {
    return false;
  }
  return Number.isFinite(Number(trimmed));
}

/**
 * How far below a panel's own timestamps a log-time value may sit before it
 * reads as a unit error rather than a deliberate out-of-range bound. Seconds
 * are 1e9 below nanoseconds and milliseconds 1e6, so a factor of 1e3 flags
 * both. Microseconds sit exactly 1e3 below, so a microsecond value is not
 * reliably flagged.
 */
const LOG_TIME_UNIT_SLACK = 1000n;

/**
 * The reason a log-time filter value is not a usable nanosecond epoch, or
 * undefined when it is.
 *
 * The search engine indexes the log-time column as nanosecond-epoch values,
 * so a value in a coarser unit is not rejected downstream: it silently selects
 * the wrong range (a `greater_than` in seconds keeps every row). `earliestTime`
 * is the earliest timestamp the panel's own data reports; when the panel
 * reports none, only the integer form is checked.
 */
export function logTimeValueProblem(
  value: string,
  earliestTime: bigint | null,
): string | undefined {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return `Log-time values are nanosecond-epoch integers; got "${value}".`;
  }
  if (earliestTime === null || earliestTime <= 0n) {
    return undefined;
  }
  const nanos = BigInt(trimmed);
  if (nanos > 0n && nanos < earliestTime / LOG_TIME_UNIT_SLACK) {
    return (
      `"${value}" is orders of magnitude below this panel's timestamps, so it ` +
      `looks like seconds, milliseconds, or microseconds rather than ` +
      `nanoseconds, and the filter would not select the intended range. ` +
      `Convert it to a nanosecond epoch and retry.`
    );
  }
  return undefined;
}
